import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  AccountCircle,
  Settings,
  Logout,
  DataObject,
  Transform,
  Email,
  Description,
  Help,
  // Psychology,
  Send,
  Close,
  SmartToy,
  Build,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/authService';
import { MFAService } from '../services/mfaService';

const DashboardPage: React.FC = () => {
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [lastAccessed, setLastAccessed] = useState<{[key: string]: string}>({});
  
  // Chatbot states
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<Array<{text: string, sender: 'user' | 'bot'}>>([
    { text: 'Hello! I\'m your RCTECH Accelerators assistant. How can I help you today?', sender: 'bot' }
  ]);
  const [userMessage, setUserMessage] = useState('');

  // Load last accessed times from localStorage on component mount
  useEffect(() => {
    const savedLastAccessed = localStorage.getItem('acceleratorLastAccessed');
    if (savedLastAccessed) {
      try {
        setLastAccessed(JSON.parse(savedLastAccessed));
      } catch (error) {
        console.error('Error parsing last accessed data:', error);
      }
    }
  }, []);

  const handleSignOut = async () => {
    try {
      // Set explicit logout flag in Firestore before signing out
      await MFAService.setLogoutFlag();
      
      // Wait a moment for the flag to be written
      await new Promise(resolve => setTimeout(resolve, 500));
      
      await signOut();
    } catch (error: any) {
      console.error('Error signing out:', error);
      // Still try to sign out even if setting the flag failed
      try {
        await signOut();
      } catch (signOutError: any) {
        console.error('Error during fallback sign out:', signOutError);
      }
    }
  };


  const handleResendVerification = async () => {
    setVerificationLoading(true);
    setVerificationMessage('');
    
    try {
      await AuthService.sendEmailVerification();
      setVerificationMessage('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      setVerificationMessage(`Error: ${error.message}`);
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    handleMenuClose();
  };

  const handleChatbotOpen = () => {
    setChatbotOpen(true);
  };

  const handleChatbotClose = () => {
    setChatbotOpen(false);
  };

  const handleSendMessage = () => {
    if (userMessage.trim() === '') return;

    // Add user message
    const newMessages = [...chatMessages, { text: userMessage, sender: 'user' as const }];
    setChatMessages(newMessages);

    // Generate bot response based on keywords
    const botResponse = generateBotResponse(userMessage.toLowerCase());
    
    // Add bot response after a short delay
    setTimeout(() => {
      setChatMessages([...newMessages, { text: botResponse, sender: 'bot' as const }]);
    }, 500);

    setUserMessage('');
  };

  const generateBotResponse = (message: string): string => {
    // Simple keyword-based responses
    if (message.includes('idmc') || message.includes('agentic') || message.includes('browser') || message.includes('automation') || message.includes('chat')) {
      return 'DataFusion-X Enterprise Knowledge Agent allows you to search and act across Confluence, Informatica IDMC (CDI, CAI, CDQ, CDP, CDGC, C360, P360), and documents. Get traceable answers with source links, lineage context, and optional actions (run jobs, retrieve assets, export reports).';
    } else if (message.includes('cdgc') || message.includes('ci/cd') || message.includes('governance')) {
      return 'The CDGC CI/CD Accelerator automates export, import, and migration of governance assets. It helps you manage your Informatica CDGC data efficiently across different environments.';
    } else if (message.includes('pdf') || message.includes('extractor') || message.includes('document')) {
      return 'The PDF Business Terms Extractor helps extract and classify glossary terms from PDF documents. It streamlines the process of digitizing business terminology.';
    } else if (message.includes('confluence') || message.includes('parser')) {
      return 'The Confluence Page Parser digitizes metadata from Confluence documentation into CDGC, making it easy to maintain governance documentation.';
    } else if (message.includes('informatica tools') || message.includes('tools') || message.includes('utilities') || message.includes('sheet conversion') || message.includes('import cleanup')) {
      return 'The Informatica Tools Accelerator provides a collection of small, useful tools for Informatica workflows. These include utilities like sheet conversion, import sheet cleanup, and other helpful tools that complement the main accelerators.';
    // } else if (message.includes('ai') || message.includes('business term')) {
    //   return 'The AI Business Term Extractor uses artificial intelligence to intelligently extract and classify business terms from various sources.';
    } else if (message.includes('help') || message.includes('support') || message.includes('contact')) {
      return 'For additional support, you can contact us at support@rctech.org or visit our documentation portal.';
    } else if (message.includes('how') || message.includes('start') || message.includes('use')) {
      return 'To get started, simply click on any accelerator card above to launch it. Each accelerator has its own interface with guided workflows. You can configure your credentials in the Settings page.';
    } else if (message.includes('settings') || message.includes('configure') || message.includes('credentials')) {
      return 'You can manage your Informatica and GitHub credentials in the Settings page. Click the Settings icon in the top navigation bar to access configuration options.';
    } else {
      return 'I can help you with information about our accelerators, how to get started, configure settings, or contact support. What would you like to know more about?';
    }
  };

  const handleAcceleratorClick = async (accelerator: any) => {
    if (currentUser && currentUser.email) {
      // Update last accessed time
      const now = new Date().toLocaleString();
      const updatedLastAccessed = {
        ...lastAccessed,
        [accelerator.id]: now
      };
      setLastAccessed(updatedLastAccessed);
      localStorage.setItem('acceleratorLastAccessed', JSON.stringify(updatedLastAccessed));

      // Get Firebase ID token and user UID only
      const token = await currentUser.getIdToken();
      const uid = currentUser.uid;
      // Open accelerator with Firebase token and UID only
      const urlWithToken = `${accelerator.url}?firebaseToken=${encodeURIComponent(token)}&firebaseUid=${encodeURIComponent(uid)}`;
      
      // Open accelerators in same tab
      if (accelerator.id === 'pdf-extractor' || accelerator.id === 'cdgc-cicd-accelerator' || accelerator.id === 'confluence-accelerator' || accelerator.id === 'idmc-browser' || accelerator.id === 'informatica-tools') {
        window.location.href = urlWithToken;
      } else {
        window.open(urlWithToken, '_blank');
      }
    }
  };

  const accelerators = [
    {
      id: 'cdgc-cicd-accelerator',
      name: 'CDGC CI/CD Accelerator',
      description: 'Automates export, import, and migration of governance assets.',
      url: 'https://cdgcaccelerator-frontend-807594002571.us-central1.run.app',
      icon: DataObject,
      color: '#1976d2',
    },
    {
      id: 'idmc-browser',
      name: 'DataFusion-X Enterprise Knowledge Agent',
      description: 'Search and act across Confluence, Informatica IDMC (CDI, CAI, CDQ, CDP, CDGC, C360, P360), and documents.',
      url: 'https://idmc-accelerator-frontend-807594002571.us-central1.run.app',
      icon: SmartToy,
      color: '#1976d2',
    },
    {
      id: 'pdf-extractor',
      name: 'PDF Content Intelligence',
      description: 'Extracts and classifies glossary terms from documents.',
      url: 'https://pdf-extractor-frontend-807594002571.us-central1.run.app',
      icon: Transform,
      color: '#1976d2',
    },
    {
      id: 'informatica-tools',
      name: 'Informatica Tools',
      description: 'Small tools and utilities for Informatica workflows like sheet conversion and import cleanup.',
      url: 'https://informatica-tools-accelerator-frontend-807594002571.us-central1.run.app',
      icon: Build,
      color: '#1976d2',
    },
    {
      id: 'confluence-accelerator',
      name: 'Confluence Page Parser',
      description: 'Digitizes metadata from documentation into CDGC.',
      url: 'https://confluence-frontend-807594002571.us-central1.run.app',
      icon: Description,
      color: '#1976d2',
    },
    // {
    //   id: 'ai-bt-extractor',
    //   name: 'AI Business Term Extractor',
    //   description: 'AI-powered intelligent extraction and classification of business terms.',
    //   url: 'https://ai-bt-accelerator-frontend-807594002571.us-central1.run.app',
    //   icon: Psychology,
    //   color: '#1976d2',
    // },
  ];

  if (!currentUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1, 
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img
              src="https://rctech.org/wp-content/uploads/2024/09/RCTECH-Logo-Empowering-Decisions-Blue.png"
              alt="RCTECH Logo"
              style={{ height: 32, marginRight: 12, cursor: 'pointer' }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1976d2',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              RCTECH Accelerators
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
            <IconButton
              size="large"
              onClick={handleSettingsClick}
              color="inherit"
              sx={{ 
                color: '#1976d2',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                }
              }}
              title="Settings"
            >
              <Settings />
            </IconButton>
            <IconButton
              size="large"
              onClick={handleMenuOpen}
              color="inherit"
              sx={{ color: '#1976d2' }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: '#1976d2', 
                  color: 'white',
                  width: 40,
                  height: 40,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                {currentUser.email ? currentUser.email.charAt(0).toUpperCase() : 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 220,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0'
                }
              }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                  {currentUser.displayName || currentUser.email}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>
                  {currentUser.email}
                </Typography>
              </Box>
              <MenuItem onClick={() => {
                handleSignOut();
                handleMenuClose();
              }} sx={{ 
                color: '#d32f2f',
                py: 1.5,
                px: 2
              }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Logout fontSize="small" sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Sign Out" 
                  primaryTypographyProps={{ fontSize: '14px' }}
                />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ pt: 8 }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Paper elevation={2} sx={{ p: 4, mb: 4, bgcolor: '#e3f2fd', borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom color="primary" sx={{ fontWeight: 600, mb: 2 }}>
            Welcome to RCTECH Accelerators Suite
          </Typography>
          <Typography variant="h6" component="h2" color="#42a5f5" sx={{ mb: 3, fontWeight: 500 }}>
            Your unified workspace for Informatica Automation.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Select an accelerator to begin. Each module is designed to optimize Informatica operations
            through automation, intelligence, and governance integration.
          </Typography>
          {!currentUser.emailVerified && (
            <Alert 
              severity="warning" 
              sx={{ mt: 3 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleResendVerification}
                  disabled={verificationLoading}
                  startIcon={verificationLoading ? <CircularProgress size={16} /> : <Email />}
                >
                  {verificationLoading ? 'Sending...' : 'Resend Verification'}
                </Button>
              }
            >
              Please verify your email address
            </Alert>
          )}
          {verificationMessage && (
            <Alert 
              severity={verificationMessage.includes('Error') ? 'error' : 'success'} 
              sx={{ mt: 2 }}
              onClose={() => setVerificationMessage('')}
            >
              {verificationMessage}
            </Alert>
          )}
        </Paper>

        {/* Accelerators Section */}
        <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
          Available Accelerators
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {accelerators.map((accelerator) => (
            <Grid item xs={12} md={4} key={accelerator.id}>
              <Card
                sx={{
                  height: '100%',
                  background: 'white',
                  color: '#1976d2',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 8px 25px rgba(25, 118, 210, 0.15)',
                    border: '1px solid #1976d2',
                  },
                }}
                onClick={() => handleAcceleratorClick(accelerator)}
              >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: '#e3f2fd',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <accelerator.icon sx={{ fontSize: 32, color: accelerator.color }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" component="h3" fontWeight="bold" sx={{ mb: 1 }}>
                        {accelerator.name}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.5, flex: 1 }}>
                    {accelerator.description}
                  </Typography>
                  {lastAccessed[accelerator.id] && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block', fontStyle: 'italic' }}>
                      Last accessed: {lastAccessed[accelerator.id]}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: accelerator.color,
                      color: 'white',
                      py: 1.5,
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                      '&:hover': {
                        bgcolor: accelerator.color,
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      },
                    }}
                    fullWidth
                  >
                    Launch Accelerator
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        </Container>
      </Box>

      {/* Help Button - Fixed Position */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          startIcon={<Help />}
          sx={{
            bgcolor: '#1976d2',
            color: 'white',
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
              bgcolor: '#1565c0',
              boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
            },
          }}
          onClick={handleChatbotOpen}
        >
          Help & Documentation
        </Button>
      </Box>

      {/* Chatbot Dialog */}
      <Dialog
        open={chatbotOpen}
        onClose={handleChatbotClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: '#1976d2',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              RCTECH Assistant
            </Typography>
          </Box>
          <IconButton
            onClick={handleChatbotClose}
            sx={{ 
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
          <List sx={{ 
            height: '400px', 
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}>
            {chatMessages.map((message, index) => (
              <ListItem
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                  p: 0,
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    maxWidth: '75%',
                    bgcolor: message.sender === 'user' ? '#1976d2' : '#ffffff',
                    color: message.sender === 'user' ? 'white' : '#1a1a1a',
                    borderRadius: 2,
                    wordWrap: 'break-word'
                  }}
                >
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    {message.text}
                  </Typography>
                </Paper>
              </ListItem>
            ))}
          </List>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Type your message..."
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={userMessage.trim() === ''}
              sx={{
                bgcolor: '#1976d2',
                minWidth: '50px',
                borderRadius: 2,
                '&:hover': {
                  bgcolor: '#1565c0',
                }
              }}
            >
              <Send />
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
